import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { InteractiveHoverButton } from "@/components/ui/interactive-hover-button";
import { supabase } from "@/integrations/supabase/client";
import { renderBoldText } from "@/lib/renderBoldText";

interface HeroData {
  fixed_text: string;
  rotating_words: string[];
  subtitle: string;
  button1_text: string;
  button1_link: string;
  button2_text: string;
  button2_link: string;
}

const defaultHero: HeroData = {
  fixed_text: "Contamos histórias, criamos",
  rotating_words: ["resultados", "conversões", "impacto", "performance", "autoridade", "posicionamento"],
  subtitle: "A Monk Studio é uma agência especializada em marketing digital, unindo estratégia e criatividade para potencializar negócios.",
  button1_text: "CONHEÇA NOSSAS SOLUÇÕES",
  button1_link: "/solucoes",
  button2_text: "FALE CONOSCO",
  button2_link: "https://api.whatsapp.com/send?phone=5521977435758",
};

function AnimatedHero() {
  const [titleNumber, setTitleNumber] = useState(0);
  const [hero, setHero] = useState<HeroData>(defaultHero);

  useEffect(() => {
    const fetchHero = async () => {
      const { data } = await supabase
        .from("hero_content")
        .select("fixed_text, rotating_words, subtitle, button1_text, button1_link, button2_text, button2_link")
        .limit(1)
        .single();
      if (data) setHero(data as HeroData);
    };
    fetchHero();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (titleNumber === hero.rotating_words.length - 1) {
        setTitleNumber(0);
      } else {
        setTitleNumber(titleNumber + 1);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, hero.rotating_words]);

  const isExternal = hero.button2_link.startsWith("http");

  return (
    <div className="flex gap-6 py-12 lg:py-20 items-center justify-center flex-col">
      <div className="flex gap-4 flex-col">
        <h1 className="text-4xl md:text-6xl max-w-3xl tracking-tighter text-center font-regular">
          <span className="text-white font-light italic">{renderBoldText(hero.fixed_text)}</span>
          <span className="relative flex w-full justify-center overflow-hidden text-center md:pb-4 md:pt-1">
            &nbsp;
            {hero.rotating_words.map((title, index) => (
              <motion.span
                key={index}
                className="absolute font-semibold"
                style={{ color: '#ffbc00' }}
                initial={{ opacity: 0, y: -150 }}
                transition={{ type: "spring", stiffness: 50 }}
                animate={
                  titleNumber === index
                    ? { y: 0, opacity: 1 }
                    : { y: titleNumber > index ? -150 : 150, opacity: 0 }
                }
              >
                {title}
              </motion.span>
            ))}
          </span>
        </h1>

        <p className="text-lg md:text-xl leading-relaxed tracking-tight text-white/70 max-w-4xl text-center">
          {renderBoldText(hero.subtitle)}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-center">
        <a href={hero.button1_link}>
          <InteractiveHoverButton text={hero.button1_text} className="border-[#ffbc00] text-white px-5 sm:px-8 text-xs sm:text-sm" />
        </a>
        <a
          href={hero.button2_link}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="inline-flex items-center gap-2 px-5 sm:px-8 py-3 rounded-full text-xs sm:text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-all whitespace-nowrap"
        >
          {hero.button2_text}
        </a>
      </div>
    </div>
  );
}

export { AnimatedHero };
